import type {
  BetAction,
  RoundResult,
  ShowMeTheCoinAction,
  ShowMeTheCoinPlayerId,
  ShowMeTheCoinState,
  SubmitCoinsAction,
} from "../types";
import {
  LEAD_MIN_SUBMIT,
  SHOW_ME_THE_COIN_ANTE,
  SHOW_ME_THE_COIN_BET_UNIT,
} from "../types";
import {
  chipWinner,
  coinValue,
  compareSubmissions,
  getOpponentPlayerId,
  inventoryCount,
  inventoryValue,
  outstandingToCall,
  removeCoins,
} from "../utils";

/** Game over by coin exhaustion: chips → remaining value → draw. */
function exhaustionGameOver(state: ShowMeTheCoinState): ShowMeTheCoinState {
  let winnerId: ShowMeTheCoinPlayerId | null;
  if (state.chips.p1 !== state.chips.p2) {
    winnerId = state.chips.p1 > state.chips.p2 ? "p1" : "p2";
  } else {
    const v1 = inventoryValue(state.inventory.p1);
    const v2 = inventoryValue(state.inventory.p2);
    winnerId = v1 === v2 ? null : v1 > v2 ? "p1" : "p2";
  }
  return { ...state, phase: "game-over", winnerId };
}

/**
 * Begin a round: pay antes and open the lead submission. Resolves to game-over
 * if a player cannot ante (0 chips) or either side is out of coins. Does NOT
 * change moveNumber — it continues the action that triggered it (init/settle).
 */
export function beginRound(state: ShowMeTheCoinState): ShowMeTheCoinState {
  const lead = state.leadPlayerId;
  const follower = getOpponentPlayerId(lead);

  if (
    inventoryCount(state.inventory.p1) < LEAD_MIN_SUBMIT ||
    inventoryCount(state.inventory.p2) < LEAD_MIN_SUBMIT
  ) {
    return exhaustionGameOver(state);
  }
  if (state.chips[lead] < SHOW_ME_THE_COIN_ANTE) {
    return { ...state, phase: "game-over", winnerId: follower };
  }
  if (state.chips[follower] < SHOW_ME_THE_COIN_ANTE) {
    return { ...state, phase: "game-over", winnerId: lead };
  }

  return {
    ...state,
    phase: "submit-lead",
    currentPlayerId: lead,
    chips: {
      p1: state.chips.p1 - SHOW_ME_THE_COIN_ANTE,
      p2: state.chips.p2 - SHOW_ME_THE_COIN_ANTE,
    },
    pot: 2 * SHOW_ME_THE_COIN_ANTE,
    contributed: { p1: SHOW_ME_THE_COIN_ANTE, p2: SHOW_ME_THE_COIN_ANTE },
    raisesUsed: 0,
    lastAggressorId: null,
    submission: { p1: null, p2: null },
  };
}

/** Settle the round (showdown or fold), then advance to the next round or end. */
function settleRound(
  state: ShowMeTheCoinState,
  folderId: ShowMeTheCoinPlayerId | null,
): ShowMeTheCoinState {
  const winnerId =
    folderId !== null
      ? getOpponentPlayerId(folderId)
      : compareSubmissions(state);

  const chips =
    winnerId === "push"
      ? {
          p1: state.chips.p1 + state.contributed.p1,
          p2: state.chips.p2 + state.contributed.p2,
        }
      : { ...state.chips, [winnerId]: state.chips[winnerId] + state.pot };

  const lastRound: RoundResult = {
    round: state.round,
    leadPlayerId: state.leadPlayerId,
    counts: {
      p1: state.submission.p1?.coins.length ?? 0,
      p2: state.submission.p2?.coins.length ?? 0,
    },
    values: {
      p1: coinValue(state.submission.p1?.coins ?? []),
      p2: coinValue(state.submission.p2?.coins ?? []),
    },
    folderId,
    winnerId,
    pot: state.pot,
  };

  const settled: ShowMeTheCoinState = {
    ...state,
    chips,
    pot: 0,
    contributed: { p1: 0, p2: 0 },
    raisesUsed: 0,
    lastAggressorId: null,
    submission: { p1: null, p2: null },
    lastRound,
    moveNumber: state.moveNumber + 1,
  };

  const gameWinner = chipWinner(chips);
  if (gameWinner !== null) {
    return { ...settled, phase: "game-over", winnerId: gameWinner };
  }

  const nextLead = getOpponentPlayerId(state.leadPlayerId);
  return beginRound({
    ...settled,
    round: state.round + 1,
    leadPlayerId: nextLead,
    currentPlayerId: nextLead,
  });
}

export function applySubmitCoins(
  state: ShowMeTheCoinState,
  action: SubmitCoinsAction,
): ShowMeTheCoinState {
  const actor = action.playerId;
  const inventory = {
    ...state.inventory,
    [actor]: removeCoins(state.inventory[actor], action.coins),
  };
  const submission = {
    ...state.submission,
    [actor]: { coins: [...action.coins] },
  };
  const toFollowPhase = state.phase === "submit-lead";

  return {
    ...state,
    inventory,
    submission,
    phase: toFollowPhase ? "submit-follow" : "bet",
    // Follower submits next; once both submitted, the lead opens the betting.
    currentPlayerId: toFollowPhase
      ? getOpponentPlayerId(actor)
      : state.leadPlayerId,
    lastAction: action,
    moveNumber: state.moveNumber + 1,
  };
}

export function applyBet(
  state: ShowMeTheCoinState,
  action: BetAction,
): ShowMeTheCoinState {
  const actor = action.playerId;
  const opponent = getOpponentPlayerId(actor);
  const base: ShowMeTheCoinState = { ...state, lastAction: action };

  switch (action.move) {
    case "check":
      // Lead's first check passes to the follower; the follower's check
      // (after the lead checked) closes betting → showdown.
      return actor === state.leadPlayerId
        ? {
            ...base,
            currentPlayerId: opponent,
            moveNumber: state.moveNumber + 1,
          }
        : settleRound(base, null);
    case "bet":
      return {
        ...base,
        chips: {
          ...state.chips,
          [actor]: state.chips[actor] - SHOW_ME_THE_COIN_BET_UNIT,
        },
        pot: state.pot + SHOW_ME_THE_COIN_BET_UNIT,
        contributed: {
          ...state.contributed,
          [actor]: state.contributed[actor] + SHOW_ME_THE_COIN_BET_UNIT,
        },
        lastAggressorId: actor,
        currentPlayerId: opponent,
        moveNumber: state.moveNumber + 1,
      };
    case "raise": {
      const amount = outstandingToCall(state) + SHOW_ME_THE_COIN_BET_UNIT;
      return {
        ...base,
        chips: { ...state.chips, [actor]: state.chips[actor] - amount },
        pot: state.pot + amount,
        contributed: {
          ...state.contributed,
          [actor]: state.contributed[actor] + amount,
        },
        raisesUsed: state.raisesUsed + 1,
        lastAggressorId: actor,
        currentPlayerId: opponent,
        moveNumber: state.moveNumber + 1,
      };
    }
    case "call": {
      const toCall = outstandingToCall(state);
      const called: ShowMeTheCoinState = {
        ...base,
        chips: { ...state.chips, [actor]: state.chips[actor] - toCall },
        pot: state.pot + toCall,
        contributed: {
          ...state.contributed,
          [actor]: state.contributed[actor] + toCall,
        },
      };
      return settleRound(called, null);
    }
    case "fold":
      return settleRound(base, actor);
  }
}

export function applyAction(
  state: ShowMeTheCoinState,
  action: ShowMeTheCoinAction,
): ShowMeTheCoinState {
  switch (action.type) {
    case "SubmitCoins":
      return applySubmitCoins(state, action);
    case "Bet":
      return applyBet(state, action);
  }
}
